import { useEffect, useState } from "react"
import { useParams } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { fetchCleanerPortal, acceptClean, declineClean, completeClean, createCleanerNote, chooseCleanDate } from "~/apis/cleanerPortal"
import { Input } from "@/components/ui/input"
import { CLEAN_KIND_COLOURS, CLEAN_KIND_LABELS } from "~/components/CleanKindFields"
import { PageSpinner } from "~/components/Spinner"
import PortalCalendar from "~/components/PortalCalendar"
import { cn } from "@/lib/utils"

function longDate(iso) {
  return new Date(`${iso}T00:00:00`).toLocaleDateString("en-AU", { weekday: "long", day: "numeric", month: "long" })
}

function monthShort(iso) {
  return new Date(`${iso}T00:00:00`).toLocaleDateString("en-AU", { month: "short" }).toUpperCase()
}

// A calendar-page tile: month on top, big day number, weekday underneath.
function DateTile({ iso, kind, tone }) {
  const date = new Date(`${iso}T00:00:00`)
  return (
    <div className={cn("w-14 shrink-0 overflow-hidden rounded-lg border text-center", tone || CLEAN_KIND_COLOURS[kind] || CLEAN_KIND_COLOURS.scheduled)}>
      <div className="py-0.5 text-[10px] font-bold tracking-wide">{monthShort(iso)}</div>
      <div className="bg-card py-1 text-foreground">
        <div className="text-2xl font-bold leading-none">{date.getDate()}</div>
        <div className="text-[10px] text-muted-foreground">{date.toLocaleDateString("en-AU", { weekday: "short" })}</div>
      </div>
    </div>
  )
}

// Property, when, and the "need to know" bullets -- the same summary the
// original portal showed on every card, with the date as a calendar tile.
function CleanSummary({ clean, notes = [] }) {
  const twoDay = clean.kind === "changeover"
  const window = clean.kind === "cleaner_chooses"
  const dayNotes = notes.filter((n) => n.on_date >= clean.window_starts_on && n.on_date <= clean.window_ends_on)

  return (
    <div>
      <div className="flex items-start gap-3">
        <div className="flex shrink-0 items-center gap-1">
          <DateTile iso={clean.window_starts_on} kind={clean.kind} />
          {(twoDay || window) && <span className="text-muted-foreground">{twoDay ? "or" : "→"}</span>}
          {(twoDay || window) && <DateTile iso={clean.window_ends_on} kind={clean.kind} />}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <p className="text-lg font-semibold leading-tight text-foreground">{clean.property_short_name}</p>
            <span className="mt-1 inline-block h-3 w-3 shrink-0 rounded-full" style={{ background: clean.property_color || "#4d7c0f" }} />
          </div>
          <p className={cn("mt-0.5 inline-block rounded border px-1.5 py-0.5 text-[11px] font-semibold", CLEAN_KIND_COLOURS[clean.kind] || CLEAN_KIND_COLOURS.scheduled)}>{CLEAN_KIND_LABELS[clean.kind] || clean.kind}</p>
          {clean.back_to_back && (
            <p className="mt-1 text-sm font-semibold text-orange-700 dark:text-orange-300">⏱ Must be done {clean.back_to_back_window} — new guests arrive that afternoon</p>
          )}
          {twoDay && (
            <p className="mt-1 text-sm text-foreground/80">Any time from 11am on {longDate(clean.window_starts_on)} to 3pm on {longDate(clean.window_ends_on)}</p>
          )}
          {window && (
            <p className="mt-1 text-sm text-foreground/80">Any day from {longDate(clean.window_starts_on)} to {longDate(clean.window_ends_on)} — pick what suits you below</p>
          )}
          {clean.kind === "scheduled" && (
            <p className="mt-1 text-sm text-foreground/80">{longDate(clean.window_starts_on)}{clean.scheduled_time ? `, ${clean.scheduled_time}` : ""}</p>
          )}
          {clean.timing_notes && (
            <p className="mt-1 text-sm font-medium text-foreground">⏰ Timing: {clean.timing_notes}</p>
          )}
        </div>
      </div>
      {clean.details?.length > 0 && (
        <ul className="mt-2 space-y-0.5">
          {clean.details.map((detail) => <li key={detail} className="text-sm text-foreground/80">• {detail}</li>)}
        </ul>
      )}
      {dayNotes.length > 0 && (
        <ul className="mt-2 space-y-0.5">
          {dayNotes.map((n) => <li key={n.id} className="text-sm text-amber-800 dark:text-amber-200">📝 {longDate(n.on_date)}: {n.body}</li>)}
        </ul>
      )}
      {clean.instructions && (
        <div className="mt-3 rounded-lg border border-brand/30 bg-brand/10 p-3">
          <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-brand">From Jeremy</p>
          <p className="whitespace-pre-wrap text-sm text-foreground">{clean.instructions}</p>
        </div>
      )}
    </div>
  )
}

const STAY_TONE = "bg-muted text-muted-foreground border-border"
const RESIDENCY_TONE = "bg-fuchsia-200 text-fuchsia-900 border-fuchsia-300 dark:bg-fuchsia-900/50 dark:text-fuchsia-100 dark:border-fuchsia-700"

// Who is turning the place over after this stay -- "you", another cleaner,
// or nobody yet -- and where that clean is up to.
function cleanLine(clean) {
  const who = clean.mine ? "you" : (clean.cleaner_name || "cleaner TBC")
  if (clean.status === "done") return `✅ Cleaned by ${who}`
  if (clean.status === "pending") return `🧹 ${clean.when_label} — waiting on ${who}`
  return `🧹 ${clean.when_label} — ${who}`
}

// One stay at the property: arrival and departure tiles, length, guests, and
// the clean(s) that follow it. Shown for every booking, not just the cleaner's.
function BookingCard({ booking, todayIso }) {
  const residency = booking.kind === "residency"
  const tone = residency ? RESIDENCY_TONE : STAY_TONE
  const staying = booking.starts_on <= todayIso
  const weeks = Math.round(booking.nights / 7)
  const length = residency && booking.nights >= 14 ? `${weeks} ${weeks === 1 ? "week" : "weeks"}` : `${booking.nights} ${booking.nights === 1 ? "night" : "nights"}`

  return (
    <div className={cn("rounded-xl border bg-card p-4 shadow-sm", staying ? "border-brand/50" : "border-border")}>
      <div className="flex items-start gap-3">
        <div className="flex shrink-0 items-center gap-1">
          <DateTile iso={booking.starts_on} tone={tone} />
          <span className="text-muted-foreground">→</span>
          <DateTile iso={booking.ends_on} tone={tone} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-1.5">
            <p className="text-lg font-semibold leading-tight text-foreground">{residency ? "Residency" : "Guests"}</p>
            {staying && <span className="rounded border border-brand/40 bg-brand/10 px-1.5 py-0.5 text-[11px] font-semibold text-brand">Staying now</span>}
          </div>
          <p className="mt-0.5 text-sm text-foreground/80">
            {length}{booking.guest_count ? ` · ${booking.guest_count} ${booking.guest_count === 1 ? "guest" : "guests"}` : ""}
          </p>
          <p className="mt-0.5 text-sm text-muted-foreground">Checkout {longDate(booking.ends_on)}</p>
        </div>
      </div>
      <ul className="mt-3 space-y-0.5 border-t border-border pt-2">
        {booking.cleans.length === 0 ? (
          <li className="text-sm text-muted-foreground">No clean booked in for this checkout yet</li>
        ) : (
          booking.cleans.map((clean) => (
            <li key={clean.id} className={cn("text-sm", clean.mine ? "font-semibold text-foreground" : "text-foreground/80")}>{cleanLine(clean)}</li>
          ))
        )}
      </ul>
    </div>
  )
}

// Jeremy's day note, shown in the list like any other item on that day.
function NoteCard({ note }) {
  const date = new Date(`${note.on_date}T00:00:00`)
  return (
    <div className="rounded-xl border border-amber-200 bg-card p-4 shadow-sm dark:border-amber-900">
      <div className="flex items-start gap-3">
        <div className="w-14 shrink-0 overflow-hidden rounded-lg border border-amber-300 bg-amber-100 text-center text-amber-900 dark:border-amber-700 dark:bg-amber-900/50 dark:text-amber-100">
          <div className="py-0.5 text-[10px] font-bold tracking-wide">{monthShort(note.on_date)}</div>
          <div className="bg-card py-1 text-foreground">
            <div className="text-2xl font-bold leading-none">{date.getDate()}</div>
            <div className="text-[10px] text-muted-foreground">{date.toLocaleDateString("en-AU", { weekday: "short" })}</div>
          </div>
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-xs font-semibold uppercase tracking-wide text-amber-700 dark:text-amber-300">📝 Note from Jeremy</p>
          <p className="mt-1 whitespace-pre-wrap text-foreground">{note.body}</p>
        </div>
      </div>
    </div>
  )
}

// "Cleaner to confirm": pick a day inside the window (and a time if you like).
function ChooseDateForm({ token, clean, onDone }) {
  const [ onDate, setOnDate ] = useState(clean.window_starts_on)
  const [ time, setTime ] = useState("")
  const [ error, setError ] = useState(null)
  const [ busy, setBusy ] = useState(false)

  async function submit(event) {
    event.preventDefault()
    setBusy(true)
    setError(null)
    try {
      const { message } = await chooseCleanDate(token, clean.id, { onDate, time })
      onDone(message)
    } catch (err) {
      setError(err.message)
      setBusy(false)
    }
  }

  return (
    <form onSubmit={submit} className="mt-3 rounded-lg border border-teal-300 bg-teal-50 p-3 dark:border-teal-800 dark:bg-teal-950/40">
      <p className="mb-2 text-sm font-semibold text-foreground">When will you come?</p>
      {/* Stacked: iOS renders native date/time pickers wider than half a card, so
          side by side they overlapped. */}
      <div className="space-y-2">
        <label className="block text-xs text-muted-foreground">
          Day
          <Input type="date" value={onDate} min={clean.window_starts_on} max={clean.window_ends_on} onChange={(e) => setOnDate(e.target.value)} className="mt-1 block h-11 w-full min-w-0 text-base" required />
        </label>
        <label className="block text-xs text-muted-foreground">
          Time (optional)
          <Input type="time" value={time} onChange={(e) => setTime(e.target.value)} className="mt-1 block h-11 w-full min-w-0 text-base" />
        </label>
      </div>
      {error && <p className="mt-2 text-sm text-destructive">{error}</p>}
      <Button type="submit" className="mt-3 w-full py-5 text-base font-semibold" disabled={busy}>Lock it in</Button>
    </form>
  )
}

function NoteForm({ token, cleanId, onSent, compact }) {
  const [ kind, setKind ] = useState("shopping_item")
  const [ body, setBody ] = useState("")
  const [ busy, setBusy ] = useState(false)

  async function submit(event) {
    event.preventDefault()
    if (!body.trim()) return
    setBusy(true)
    try {
      const { message } = await createCleanerNote(token, { cleanId, kind, body })
      onSent(message)
      setBody("")
    } catch (err) {
      onSent(err.message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <form onSubmit={submit} className={compact ? "mt-3" : ""}>
      <Textarea value={body} onChange={(event) => setBody(event.target.value)} rows={2} required
        placeholder={kind === "shopping_item" ? "e.g. Dishwashing liquid is nearly out" : "e.g. The back gate latch is loose"} className="text-base" />
      <div className="mt-2 flex items-center gap-3">
        <Select value={kind} onValueChange={setKind}>
          <SelectTrigger size="sm"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="shopping_item">Something to buy</SelectItem>
            <SelectItem value="note">General note</SelectItem>
          </SelectContent>
        </Select>
        <Button type="submit" size="sm" className="ml-auto" disabled={busy || !body.trim()}>Send</Button>
      </div>
    </form>
  )
}

function FinishForm({ token, clean, onDone }) {
  const [ conditionOk, setConditionOk ] = useState(null)
  const [ notes, setNotes ] = useState("")
  const [ error, setError ] = useState(null)
  const [ busy, setBusy ] = useState(false)

  async function submit(event) {
    event.preventDefault()
    if (conditionOk === null) {
      setError("Please tell us whether the property was left okay.")
      return
    }
    setBusy(true)
    setError(null)
    try {
      const { message } = await completeClean(token, clean.id, { conditionOk, conditionNotes: notes })
      onDone(message)
    } catch (err) {
      setError(err.message)
      setBusy(false)
    }
  }

  return (
    <form onSubmit={submit} className="mt-4 border-t border-border pt-4">
      <h3 className="font-semibold text-foreground">Finished the clean?</h3>
      <p className="mb-2 mt-1 text-sm text-foreground/80">Was the property left in good condition?</p>
      <div className="mb-3 space-y-2">
        {[ [ true, "Yes, all good 👍" ], [ false, "There was a problem 👎" ] ].map(([ value, label ]) => (
          <label key={String(value)} className={cn("flex items-center gap-3 rounded-lg border p-3 text-base", conditionOk === value ? "border-brand bg-brand/10" : "border-border")}>
            <input type="radio" name={`condition-${clean.id}`} checked={conditionOk === value} onChange={() => setConditionOk(value)} className="h-5 w-5" />
            <span>{label}</span>
          </label>
        ))}
      </div>
      <Textarea value={notes} onChange={(event) => setNotes(event.target.value)} rows={2} placeholder="Anything Jeremy should know? (optional)" className="mb-3 text-base" />
      {error && <p className="mb-2 text-sm text-destructive">{error}</p>}
      <Button type="submit" className="w-full py-6 text-base font-semibold" disabled={busy}>Mark clean as done</Button>
    </form>
  )
}

export default function CleanerPortal() {
  const { token } = useParams()
  const [ data, setData ] = useState(null)
  const [ failed, setFailed ] = useState(false)
  const [ message, setMessage ] = useState(null)
  const [ openClean, setOpenClean ] = useState(null)
  const [ view, setView ] = useState(() => {
    try { return localStorage.getItem("portalView") || "list" } catch { return "list" }
  })

  function switchView(next) {
    setView(next)
    try { localStorage.setItem("portalView", next) } catch { /* private window */ }
  }

  // From the calendar, jump to the clean's card in the list.
  function pickClean(clean) {
    switchView("list")
    setOpenClean(clean.id)
    setTimeout(() => document.getElementById(`clean-${clean.id}`)?.scrollIntoView({ block: "center", behavior: "smooth" }), 50)
  }

  useEffect(() => { load() }, [ token ])

  function load() {
    fetchCleanerPortal(token).then(setData).catch(() => setFailed(true))
  }

  function flash(text) {
    setMessage(text)
    window.scrollTo({ top: 0, behavior: "smooth" })
    load()
  }

  async function act(action, cleanId) {
    try {
      const { message: text } = await action(token, cleanId)
      flash(text)
    } catch (err) {
      flash(err.message)
    }
  }

  if (failed) {
    return (
      <div className="mx-auto max-w-lg px-4 py-16 text-center">
        <p className="text-lg font-semibold text-foreground">This link isn't active any more.</p>
        <p className="mt-2 text-muted-foreground">Ask Jeremy to send you a new one.</p>
      </div>
    )
  }
  if (!data) return <PageSpinner />

  const { cleaner, pending, upcoming, recent, notes, calendar = [], property_notes: propertyNotes = [], bookings = [] } = data
  const todayIso = new Date().toLocaleDateString("en-CA")
  // Upcoming cleans and Jeremy's day notes in one chronological list.
  const timeline = [
    ...upcoming.map((clean) => ({ kind: "clean", date: clean.window_starts_on, key: `clean-${clean.id}`, clean })),
    ...propertyNotes.filter((n) => n.on_date >= todayIso).map((note) => ({ kind: "note", date: note.on_date, key: `note-${note.id}`, note })),
  ].sort((a, b) => a.date.localeCompare(b.date))

  return (
    <div className="mx-auto max-w-lg px-4 py-6">
      <header className="mb-6">
        <h1 className="text-2xl font-bold text-foreground">Hi {cleaner.first_name} 👋</h1>
        <p className="mt-1 text-sm text-muted-foreground">Your cleans at {cleaner.property_name}</p>
      </header>

      {message && (
        <p className="mb-4 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm font-medium text-green-900 dark:border-green-900 dark:bg-green-950/40 dark:text-green-100">{message}</p>
      )}

      <div className="mb-4 flex items-center rounded-lg bg-muted p-0.5 text-sm font-semibold">
        {[ [ "list", "List" ], [ "calendar", "Calendar" ], [ "bookings", "Bookings" ] ].map(([ value, label ]) => (
          <button
            key={value}
            type="button"
            onClick={() => switchView(value)}
            aria-pressed={view === value}
            className={cn("flex-1 rounded-md px-3 py-2", view === value ? "bg-card text-foreground shadow-sm" : "text-muted-foreground")}
          >
            {label}
          </button>
        ))}
      </div>

      {view === "calendar" && <div className="mb-8"><PortalCalendar cleans={calendar} notes={propertyNotes} onPick={pickClean} /></div>}

      {view === "bookings" && (
        <section className="mb-8">
          <h2 className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Upcoming bookings</h2>
          <p className="mb-3 text-sm text-muted-foreground">Every stay at {cleaner.property_name}, whoever is doing the clean.</p>
          {bookings.length === 0 ? (
            <p className="rounded-xl border border-border bg-card p-4 text-sm text-muted-foreground">No bookings coming up.</p>
          ) : (
            <div className="space-y-3">
              {bookings.map((booking, index) => {
                // The tiles show day and month only, and the list runs well into
                // next year, so head each new month with its name and year.
                const month = booking.starts_on.slice(0, 7)
                const newMonth = index === 0 || bookings[index - 1].starts_on.slice(0, 7) !== month
                return (
                  <div key={booking.id} className="space-y-3">
                    {newMonth && (
                      <h3 className={cn("text-sm font-semibold text-foreground", index > 0 && "pt-3")}>
                        {new Date(`${booking.starts_on}T00:00:00`).toLocaleDateString("en-AU", { month: "long", year: "numeric" })}
                      </h3>
                    )}
                    <BookingCard booking={booking} todayIso={todayIso} />
                  </div>
                )
              })}
            </div>
          )}
        </section>
      )}

      {view === "list" && pending.length > 0 && (
        <section className="mb-8">
          <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-amber-700 dark:text-amber-300">Can you do these?</h2>
          <div className="space-y-3">
            {pending.map((clean) => (
              <div key={clean.id} id={`clean-${clean.id}`} className="rounded-xl border border-amber-200 bg-card p-4 shadow-sm dark:border-amber-900">
                <CleanSummary clean={clean} notes={propertyNotes} />
                {clean.kind === "cleaner_chooses" ? (
                  <>
                    <ChooseDateForm token={token} clean={clean} onDone={flash} />
                    <button type="button" className="mt-2 w-full text-center text-sm text-muted-foreground underline" onClick={() => act(declineClean, clean.id)}>Can't do this one</button>
                  </>
                ) : (
                  <div className="mt-3 flex gap-2">
                    <Button className="flex-1 bg-green-600 py-6 text-base font-semibold text-white hover:bg-green-700" onClick={() => act(acceptClean, clean.id)}>Yes, I can ✓</Button>
                    <Button variant="secondary" className="flex-1 py-6 text-base font-semibold" onClick={() => act(declineClean, clean.id)}>Can't do it ✕</Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {view === "list" && <section className="mb-8">
        <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Upcoming cleans</h2>
        {timeline.length === 0 ? (
          <p className="rounded-xl border border-border bg-card p-4 text-sm text-muted-foreground">Nothing booked in right now.</p>
        ) : (
          <div className="space-y-3">
            {timeline.map((item) => {
              if (item.kind === "note") return <NoteCard key={item.key} note={item.note} />
              const clean = item.clean
              const open = openClean === clean.id
              return (
                <div key={clean.id} id={`clean-${clean.id}`} className={cn("rounded-xl border bg-card p-4 shadow-sm", open ? "border-brand" : "border-border")}>
                  <CleanSummary clean={clean} notes={propertyNotes} />
                  {clean.kind === "cleaner_chooses" && <ChooseDateForm token={token} clean={clean} onDone={flash} />}
                  {clean.property_info && open && (
                    <div className="mt-3 rounded-lg border border-border bg-muted/50 p-3">
                      <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">About this property</p>
                      <p className="whitespace-pre-wrap text-sm text-foreground">{clean.property_info}</p>
                    </div>
                  )}
                  {open ? (
                    <>
                      <FinishForm token={token} clean={clean} onDone={(text) => { setOpenClean(null); flash(text) }} />
                      <details className="mt-4">
                        <summary className="cursor-pointer text-sm text-muted-foreground">Anything to buy / note for this clean?</summary>
                        <NoteForm token={token} cleanId={clean.id} onSent={flash} compact />
                      </details>
                      <button type="button" className="mt-3 text-sm text-muted-foreground underline" onClick={() => setOpenClean(null)}>Close</button>
                    </>
                  ) : (
                    <button type="button" className="mt-2 text-sm font-medium text-brand" onClick={() => setOpenClean(clean.id)}>Tap to see details or mark as done →</button>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </section>}

      <section className="mb-8">
        <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Need anything bought? Noticed something?</h2>
        <div className="rounded-xl border border-border bg-card p-4 shadow-sm">
          <NoteForm token={token} onSent={flash} />
          {notes.length > 0 && (
            <ul className="mt-4 space-y-1 border-t border-border pt-3">
              {notes.map((note) => (
                <li key={note.id} className="text-sm text-foreground/80">
                  <span className="text-muted-foreground">{note.kind === "shopping_item" ? "🛒" : "📝"}</span> {note.body}
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>

      {recent.length > 0 && (
        <section>
          <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Recently done</h2>
          <ul className="space-y-1">
            {recent.map((clean) => <li key={clean.id} className="px-1 text-sm text-muted-foreground">✅ {clean.property_short_name} — {clean.when_label}</li>)}
          </ul>
        </section>
      )}
    </div>
  )
}
